import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { postDetails } from "../../services/http/http.service";
import APIList from "../../utils/constants/APIList";
import CommonTable, { TableColumn } from "../../components/Table/CommonTable";
import LayoutContainer from "../../components/Containers/LayoutContainer";
import PageHeader from "../../components/PageHeader/PageHeader";
import {
  AlignStart,
  ArrowDownSquare,
  ArrowDownSquareFill,
  ArrowLeftSquareFill,
  Download,
  HandThumbsUp,
  Trash3Fill,
} from "react-bootstrap-icons";
import Modal from "react-bootstrap/Modal";
import { useAppDispatch } from "../../store/storeRequests";
import { hideLoader, showLoader } from "../../store/Loader/loader.slice";
import { showToast, hideToast } from "../../store/Toast/toast.slice";
import { Button } from "react-bootstrap";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import format from "date-fns/format";
import defaultProfile from "../../assets/default-profile.png";
import formatDate from "../../utils/helpers/formatDate";
import sortByRecentDate from "../../utils/helpers/sortByRecentDate";


interface Photo {
  id: number;
  userName: string;
  profilePic: string;
  title: string;
  filename: string;
  likes: number;
  date: string;
  status: number;
  profile: string;
}

interface LikedUser {
  id: number;
  profile: string;
  username: string;
  created: string;
}

const PhotoChallengeDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<Photo[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [likedUsers, setLikedUsers] = useState<LikedUser[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        dispatch(showLoader("Loading Data..."));
        const response = await postDetails(
          APIList.GET_PHOTO_DETAILS_BY_FOLDER_ID,
          { id }
        );
        setData(response.data);
        console.log(response.data);
      } catch (error) {
        console.error("Error fetching photos:", error);
      } finally {
        dispatch(hideLoader());
      }
    };

    fetchPhotos();
  }, [id]);

  const redirect = () => {
    navigate("/photo-challenge");
  };

  const handleDownloadAll = async () => {
    try {
      dispatch(showLoader("Generating ZIP..."));
      const zip = new JSZip();
      const folder = zip.folder("photos");

      if (data.length === 0) {
        console.error("No photos to download.");
        dispatch(hideLoader());
        return;
      }

      const photoFetchPromises = data.map(async (photo, index) => {
        try {
          const imageUrl = photo.filename;
          const filenameObj = photo.filename.split('/');
          photo.title = filenameObj[filenameObj.length -1];

          if (!imageUrl) {
            console.error(`No image URL found for photo: ${photo.title}`);
            return;
          }

          const response = await fetch(imageUrl);

          if (!response.ok) {
            console.error(
              `Error downloading photo ${photo.title}: ${response.statusText}`
            );
            return;
          }

          const imageBlob = await response.blob();
          const fileName = `${photo.title || `photo_${index + 1}`}.jpg`;

          folder?.file(fileName, imageBlob);
        } catch (error) {
          console.error(`Error downloading photo ${photo.title}:`, error);
        }
      });

      await Promise.all(photoFetchPromises);

      if (!folder || Object.keys(folder.files).length === 0) {
        console.error("No valid photos to download.");
        dispatch(hideLoader());
        return;
      }

      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "photos.zip");
    } catch (error) {
      console.error("Error generating ZIP file:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleImagePreview = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedImage("");
    setLikedUsers([]);
  };

  const deleteItemById = async (item: Photo) => {
    try {
      const response = await postDetails(APIList.DELETE_PHOTO, {
        id: item.id,
      });

      if (response.status === "Failed") {
        console.log("Cannot delete folder");
        return;
      }

      setData((prevData) => prevData.filter((row) => row.id !== item.id));
      console.log("Folder deleted");
      dispatch(showToast("Deleted Succesfully."));
      setTimeout(() => dispatch(hideToast()), 3000);
    } catch (error) {
      console.error("Error deleting folder:", error);
    }
  };

  const handleLikes = async (item: Photo) => {
    try {
      setLoading(true);
      const response = await postDetails(APIList.GET_PHOTO_LIKE_DATA, {
        id: item.id,
      });

      if(Array.isArray(response.data)){
      
        // const sortedLikes: LikedUser[] = sortByRecentDate(response.data, "created")
        // setLikedUsers(sortedLikes);
        setLikedUsers(response.data);
      } else{
        setLikedUsers([]);
      }
      setShowModal(true);
      console.log("likes", response.data);
    } catch (error) {
      console.error("Error fetching liked users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (selectedIds: Set<number>) => {
    console.log(selectedIds);
    setSelectedItems(selectedIds);
  };

  const handleBulkDelete = async () => {
    try {
      for (const id of selectedItems) {
        const itemToDelete = data.find((item) => item.id === id);
        if (itemToDelete) {
          await deleteItemById(itemToDelete);
        }
      }
      setSelectedItems(new Set());
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting items:", error);
    }
  };

  const handleCloseDeleteModal = () => setShowDeleteModal(false);

  const buttons = [
    {
      label: "Delete",
      theme: "danger",
      icon: <Trash3Fill />,
      onClick: () => setShowDeleteModal(true),
      disabled: selectedItems.size === 0,
    },
    {
      label: "Download All Images",
      theme: "danger",
      icon: <Download />,
      onClick: handleDownloadAll,
    },
    {
      label: "Back to Main List",
      theme: "danger",
      icon: <ArrowLeftSquareFill />,
      onClick: redirect,
    },
  ];

  const columns: TableColumn[] = [
    { key: "username", label: "User Name", sortable: true, dataType: "string" },
    {
      key: "profile",
      label: "Profile Pic",
      dataType: "image",
      render: (item: Photo) => (
        <img
          src={item.profile ? item.profile : defaultProfile}
          alt={item.userName}
          style={{
            width: "40px",
            height: "40px",
            objectFit: "cover",
          }}
        />
      ),
    },
    { key: "caption", label: "Title", sortable: true, dataType: "string" },
    {
      key: "",
      label: "Image Preview",
      dataType: "button",
      button: {
        label: "View",
        onClick: (item) => handleImagePreview(item.filename),
      },
    },
    {
      key: "likecount",
      label: "Likes",
      dataType: "button",
      sortable: true,
      // button: {
      //   label: (item) => (
      //     <div style={{ display: "flex", alignItems: "center" }}>
      //       <HandThumbsUp style={{ marginRight: "5px" }} />
      //       {item.likecount || 0}
      //     </div>
      //   ),
      //   onClick: handleLikes,
      // },
      render: (item)=>(
        <div 
        style={{ display: "flex",
           alignItems: "center",
           justifyContent: "center",
           gap: "2",
           width: "58px",
           margin: "auto",
           padding: "6px 12px",
           color: "#fff",
           backgroundColor: "rgb(255, 204, 0)",
           cursor: "pointer"
          }}
        onClick={()=> handleLikes(item)}
        // className="btn-secondary"
        >
          <HandThumbsUp style={{ marginRight: "5px" }} />
            {item.likecount || 0}
          </div>
      )
    },
    {
      key: "created",
      label: "Date",
      dataType: "string",
      sortable: true,
      render: (record) =>
        new Date(record.created).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
    },
  ];

  return (
    <LayoutContainer title="View Photos - Folder name">
      <div className="page-header d-flex align-items-center justify-content-left dashboard-container h-100">
        <h2>
          <i className="page-header-icon fa fa-home"></i>
          View Photos
        </h2>
      </div>
      <PageHeader buttons={buttons} />
      <CommonTable<Photo>
        data={data}
        columns={columns}
        onCheckboxChange={handleCheckboxChange}
        onDelete={deleteItemById}
        pagination={true}
        rowsPerPage={10}
        actions={{
          delete: true,
          checkbox: true,
        }}
      />
      {/* Modal Component */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        dialogClassName={selectedImage ? "" : "view-modal-size"}
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedImage ? "Image Preview" : "Likes"}</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            display: "flex",
            justifyContent: selectedImage ? "center" : "flex-start",
            alignItems: "start",
            padding: selectedImage ? 0 : "20px",
            height: "300px",
            overflowY: "auto",
          }}
        >
          {selectedImage ? (
            <img
              src={selectedImage}
              alt="Preview"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "60vh",
                objectFit: "contain",
              }}
            />
          ) : (
            <>
              {loading ? (
                <p>Loading...</p>
              ) : likedUsers.length > 0 ? (
                <ul style={{ listStyleType: "none", padding: 0 ,
                  alignSelf:"start"
                }}>
                  {likedUsers.map((user) => (
                    <li
                      key={user.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "10px",
                      }}
                    >
                      <img
                        src={
                          user.profile ? user.profile : defaultProfile
                        }
                        alt={user.username}
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          marginRight: "10px",
                        }}
                      />
                      <div className="d-flex flex-column">
                      <span className="fw-semibold font-16 mb-1">{user.username ? user.username : "Username not available"}</span>
                      <span className="font-12">{ user.created 
                      ? new Date(user.created).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                      : "Created date not available"}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No data found.</p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {/* <button className="btn-danger" onClick={handleCloseModal}>
            Close
          </button> */}
          <Button
            variant="danger"
            onClick={handleCloseModal}
          >
           Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        centered
        className="deleteAll-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete?</p>
          <Button className="btn-danger" onClick={handleBulkDelete}>
            Delete
          </Button>
          <Button
            className="btn-warning text-white"
            onClick={handleCloseDeleteModal}
          >
            Cancel
          </Button>
        </Modal.Body>
      </Modal>
    </LayoutContainer>
  );
};

export default PhotoChallengeDetails;
